/* ============================================================================
   MEET OUR CORE LINE — Core Products (D 3468:241474 · M 3427:241241)

   Desktop: the style names are tabs. Hovering one shows its navy ghost in a
   fixed slot at the right edge of the list column, level with the row —
   Satisfy Pro Team's hover reveal: 200ms ease-out fade, no scale, no
   cursor-follow. Clicking (or Enter / Space) makes it the right column:
   name, colour · pocket · fabric, three plates, copy and SHOP NOW, which
   crossfade so nothing hard-swaps.
   Mobile: the same names as an accordion — one open at a time, the open
   one's content under its label (Figma's Catarina state); tapping the open
   name closes it. The closing drawer, the opening drawer and the page's
   scroll run on one eased clock, so the opened row glides to 80px from the
   top (Satisfy) instead of jumping there.
   ========================================================================== */

import { animate, MO, state, play, tween, track, $, $$, mqDesktop } from '../10-pdp-prototype/mo.js';
import { tm } from '../10-pdp-prototype/ui.js';
import { DATA } from './data.js';
import { gender, onGender } from './gender.js';

const root = $('[data-core]');
const listing = $('[data-core-listing]', root);
const panel = $('[data-core-panel]', root);
const left = $('.ncl-core__left', root);
let selected = { women: 'catarina', men: null };
let items = [];

const crop = (c) => (c ? ` style="position:absolute;max-width:none;width:${c.w}%;height:${c.h}%;left:${c.l}%;top:${c.t}%"` : '');
/* Figma's two-line heading: the style, then what it is ("Catarina / Scrub Top",
   "Isabel High-Rise / Wide-Leg Scrub Pant") */
function headingLines(h) {
  const w = String(h).split(/\s+/);
  const n = Math.max(1, Math.floor(w.length / 2));
  return `${w.slice(0, n).join(' ')}<br>${w.slice(n).join(' ')}`;
}

function plate(p, key, shape) {
  const pl = p.plates?.[key];
  if (!pl) return `<div class="ncl-core__plate ncl-core__plate--${shape}" data-core-plate></div>`;
  if (pl.kind === 'video') {
    return `<div class="ncl-core__plate ncl-core__plate--${shape} ncl-core__plate--spin" data-core-plate>
      <video muted loop playsinline preload="metadata" poster="${pl.poster}" data-core-video aria-label="${p.label} spinning on a turntable"><source src="${pl.src}" type="video/mp4"></video></div>`;
  }
  if (pl.kind === 'ghost') {
    return `<div class="ncl-core__plate ncl-core__plate--${shape} ncl-core__plate--ghost" data-core-plate>
      <img src="${pl.src}" alt="${p.label} in Navy, flat" loading="lazy" decoding="async"></div>`;
  }
  const fit = pl.fit === 'contain' ? ' style="object-fit:contain"' : '';
  return `<div class="ncl-core__plate ncl-core__plate--${shape}" data-core-plate${pl.bg ? ` style="background:${pl.bg}"` : ''}>
    <img src="${pl.src}" alt="" loading="lazy" decoding="async"${pl.crop ? crop(pl.crop) : fit}></div>`;
}

const meta = (p) => [p.colorCount ? `${p.colorCount} colors` : null, p.pockets, p.fabric?.toLowerCase()].filter(Boolean);
const shopNow = (p) => {
  const ext = /^https?:/.test(p.href);
  return `<a class="ui-btn ui-btn--solid ncl-core__shop" href="${p.href}"${ext ? ' target="_blank" rel="noopener"' : ''} data-hover-btn>Shop Now</a>`;
};

function panelHTML(p) {
  return `
    <div class="ncl-panel" data-core-panel-inner>
      <div class="ncl-panel__head" data-core-reveal>
        <p class="ncl-panel__name">${headingLines(p.heading)}</p>
        <p class="ncl-panel__meta">${meta(p).map((m) => `<span>${m}</span>`).join('')}</p>
      </div>
      <div class="ncl-panel__grid">
        <div class="ncl-panel__row">
          <div data-core-reveal>${plate(p, 'first', 'wide')}</div>
          <div data-core-reveal>${plate(p, 'detail', 'wide')}</div>
        </div>
        <div class="ncl-panel__row ncl-panel__row--bottom">
          <div class="ncl-panel__copy" data-core-reveal>
            <p class="ncl-core__desc">${tm(p.description)}</p>
            ${shopNow(p)}
          </div>
          <div class="ncl-panel__model" data-core-reveal>${plate(p, 'model', 'tall')}</div>
        </div>
      </div>
    </div>`;
}

function drawerHTML(p) {
  return `
    <p class="ncl-core__meta-line">${meta(p).join(' | ')}</p>
    ${plate(p, 'model', 'tall')}
    <div class="ncl-core__pair">${plate(p, 'first', 'wide')}${plate(p, 'detail', 'wide')}</div>
    <p class="ncl-core__desc">${tm(p.description)}</p>
    ${shopNow(p)}`;
}

function render(g) {
  const list = DATA[g].core;
  if (!selected[g] || !list.some((p) => p.id === selected[g])) selected[g] = list[0]?.id || null;
  const groups = [['tops', 'scrub tops'], ['pants', 'scrub pants']];
  listing.innerHTML = groups.map(([id, label]) => {
    const rows = list.filter((p) => p.group === id);
    if (!rows.length) return '';
    return `
      <div class="ncl-core__group" data-core-group="${id}">
        <p class="ncl-core__group-label" data-core-item>${label}</p>
        ${rows.map((p) => `
          <div class="ncl-core__item" data-core-item data-id="${p.id}">
            <button class="ncl-core__tab" type="button" id="ncl-core-tab-${p.id}" aria-expanded="false"
              aria-controls="ncl-core-panel ncl-core-d-${p.id}" data-core-tab="${p.id}">
              <span class="trim">${p.label}</span>
            </button>
            ${p.ghost ? `<span class="ncl-core__ghost" aria-hidden="true" data-core-ghost-for="${p.id}"><img src="${p.ghost}" alt="" loading="lazy" decoding="async"></span>` : ''}
            <div class="ncl-core__drawer" id="ncl-core-d-${p.id}" role="region" aria-labelledby="ncl-core-tab-${p.id}">
              <div class="ncl-core__drawer-inner"><div class="ncl-core__drawer-body">${drawerHTML(p)}</div></div>
            </div>
          </div>`).join('')}
      </div>`;
  }).join('');
  items = $$('.ncl-core__item', listing);
  panel.id = 'ncl-core-panel';
  $('[data-core-sets]', root).href = g === 'men' ? '/experiments/10-pdp-prototype/mens-set.html' : '/experiments/14-plp-scrub-sets/';
  placeGhosts();
  select(selected[g], { animate: false, scroll: false });
}

/* the ghost slot: the right edge of the list column, the row's height
   centred a little high (Satisfy: 54 above a 32 row, 26 below) */
function placeGhosts() {
  if (!mqDesktop.matches) return;
  const lr = left.getBoundingClientRect();
  items.forEach((it) => {
    const slot = $('.ncl-core__ghost', it);
    if (!slot) return;
    const ir = it.getBoundingClientRect();
    const w = 150;
    const h = 200;
    slot.style.left = `${lr.right - ir.left - w - 40}px`;
    slot.style.top = `${$('.ncl-core__tab', it).offsetHeight * 0.5 - h * 0.6}px`;
  });
}

function playVideos(scope) {
  $$('video[data-core-video]', root).forEach((v) => {
    const on = scope.contains(v) && !state.reduced;
    if (on) v.play().catch(() => {}); else v.pause();
  });
}

let swapping = null;
async function select(id, { animate = true, scroll = true } = {}) {
  const g = gender();
  const p = DATA[g].core.find((x) => x.id === id);
  if (!p) return;
  const desktop = mqDesktop.matches;
  const prev = selected[g];
  const same = prev === id && !animate;
  selected[g] = id;

  items.forEach((it) => $('.ncl-core__tab', it).setAttribute('aria-expanded', String(it.dataset.id === id)));
  if (!desktop) accordion($(`[data-id="${id}"]`, listing), { animate, scroll });
  else items.forEach((it) => it.toggleAttribute('data-open', it.dataset.id === id));

  /* desktop: the right column */
  if (desktop || !panel.innerHTML) {
    const token = (swapping = {});
    const old = panel.firstElementChild;
    if (old && animate) {
      await play(old, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
      if (token !== swapping) return;
    }
    if (!same || !panel.innerHTML || animate) panel.innerHTML = panelHTML(p);
    if (animate) {
      const parts = $$('[data-core-reveal]', panel);
      tween(parts, { opacity: [0, 1], y: [MO.riseSm, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: (i) => i * MO.stagTight });
    }
  }
  playVideos(desktop ? panel : $(`[data-id="${id}"]`, listing) || panel);
}

/* ---- the mobile accordion ------------------------------------------------
   One row opens (or none), every other row closes — including one still
   closing from an interrupted tap — and the scroll that brings the opened
   label to 80px from the top is worked out in the layout the motion ends
   on, then run on the same clock as the drawers. Heights are inline only
   while they move; at rest the stylesheet says 0 or auto. */
const ACC = { duration: 0.5, ease: [0.4, 0, 0.2, 1] };
let accRun = null;
function accordion(openIt, { animate: animated = true, scroll = true } = {}) {
  accRun?.stop?.();
  document.documentElement.style.overflowAnchor = '';
  const drawer = (it) => $('.ncl-core__drawer', it);
  /* measure everything in the layout as it is now, before the open state flips */
  const now = new Map(items.map((it) => [it, drawer(it).getBoundingClientRect().height]));
  const closing = items.filter((it) => it !== openIt && now.get(it) > 0.5);
  const topNow = openIt ? openIt.getBoundingClientRect().top : 0;
  items.forEach((it) => it.toggleAttribute('data-open', it === openIt));
  const settle = () => items.forEach((it) => { drawer(it).style.height = ''; });
  if (!animated || state.reduced) {
    settle();
    if (scroll && openIt) {
      const top = openIt.getBoundingClientRect().top;
      if (Math.abs(top - 80) > 24) window.scrollTo({ top: scrollY + top - 80, behavior: 'instant' });
    }
    return;
  }
  const full = openIt ? $('.ncl-core__drawer-inner', drawer(openIt)).scrollHeight : 0;
  const from = openIt ? now.get(openIt) : 0;
  /* where the page should end: drawers above the opened row give up their height */
  const y0 = scrollY;
  let y1 = y0;
  if (scroll && openIt) {
    const above = closing.filter((it) => it.compareDocumentPosition(openIt) & Node.DOCUMENT_POSITION_FOLLOWING).reduce((a, it) => a + now.get(it), 0);
    const finalTop = topNow - above;
    if (Math.abs(finalTop - 80) > 24) y1 = Math.max(0, y0 + finalTop - 80);
  }
  /* the browser's scroll anchoring would shift the page as a drawer above
     collapses, on top of this scroll — off while the accordion moves */
  const html = document.documentElement;
  html.style.overflowAnchor = 'none';
  const travel = scroll && openIt && y1 !== y0 ? Math.abs(topNow - 80) : 0; // how far the tapped label moves
  closing.forEach((it) => {
    drawer(it).style.height = `${now.get(it)}px`;
    animate($('.ncl-core__drawer-body', it), { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit });
  });
  if (openIt) {
    drawer(openIt).style.height = `${from}px`;
    animate($('.ncl-core__drawer-body', openIt), { opacity: [0, 1] }, { duration: 0.36, delay: 0.12, ease: MO.eStandard });
  }
  accRun = track(animate(0, 1, {
    ...ACC,
    duration: ACC.duration + Math.min(0.25, travel / 2400), // a longer glide takes a little longer
    onUpdate: (t) => {
      closing.forEach((it) => { drawer(it).style.height = `${now.get(it) * (1 - t)}px`; });
      if (openIt) drawer(openIt).style.height = `${from + (full - from) * t}px`;
      if (y1 !== y0) window.scrollTo({ top: y0 + (y1 - y0) * t, behavior: 'instant' });
    },
    onComplete: () => { settle(); html.style.overflowAnchor = ''; },
  }));
}

function bind() {
  listing.addEventListener('click', (e) => {
    const t = e.target.closest('[data-core-tab]');
    if (!t) return;
    const id = t.dataset.coreTab;
    if (!mqDesktop.matches && selected[gender()] === id) {
      /* tapping the open row closes it (the ×) */
      selected[gender()] = null;
      t.setAttribute('aria-expanded', 'false');
      accordion(null, { scroll: false });
      playVideos(document.createElement('div'));
      return;
    }
    select(id);
  });
  /* hover reveal — only where hover is real */
  const hoverable = matchMedia('(hover: hover) and (pointer: fine)');
  listing.addEventListener('pointerover', (e) => {
    if (!hoverable.matches || !mqDesktop.matches) return;
    const it = e.target.closest('.ncl-core__item');
    items.forEach((x) => $('.ncl-core__ghost', x)?.toggleAttribute('data-on', x === it));
  });
  listing.addEventListener('pointerleave', () => items.forEach((x) => $('.ncl-core__ghost', x)?.removeAttribute('data-on')));
  listing.addEventListener('focusin', (e) => {
    if (!mqDesktop.matches) return;
    const it = e.target.closest('.ncl-core__item');
    items.forEach((x) => $('.ncl-core__ghost', x)?.toggleAttribute('data-on', x === it && e.target.matches(':focus-visible')));
  });
  listing.addEventListener('focusout', () => items.forEach((x) => $('.ncl-core__ghost', x)?.removeAttribute('data-on')));
  addEventListener('resize', placeGhosts);
  mqDesktop.addEventListener('change', () => { placeGhosts(); select(selected[gender()] || DATA[gender()].core[0]?.id, { animate: false, scroll: false }); });
}

export function initCore() {
  render(gender());
  bind();
  onGender((g, source) => {
    const touched = source === root;
    if (!touched) { render(g); return; }
    const out = play(listing, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit });
    Promise.resolve(out?.finished).then(() => {
      render(g);
      tween($$('[data-core-item]', listing), { opacity: [0, 1], y: [MO.riseXs, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: (i) => i * MO.stagTight });
      play(listing, { opacity: [0, 1] }, { duration: MO.instant });
      const parts = $$('[data-core-reveal]', panel);
      tween(parts, { opacity: [0, 1], y: [MO.riseSm, 0] }, { duration: MO.slow, ease: MO.eEntrance, delay: (i) => i * MO.stagTight });
    });
  });
}

/* the scroll reveal's targets (main.js) */
export const coreRevealTargets = () => [...$$('[data-core-item]', listing), ...$$('[data-core-reveal]', panel)];
